import { Button, FlexBox, Text } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import type { RoutedThroughFlowEntry } from 'lib-shared-legacy-types';
import { ConversationFlowActivitiesNode } from 'lib-shared-types-api';
import { type ReactNode, useMemo } from 'react';

import { conversationsApi } from 'apis/conversations';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { LogEntry } from './logEntry';
import {
  RoutedThroughAgentAvailability,
  RoutedThroughAnnouncement,
  RoutedThroughAutoReply,
  RoutedThroughBusinessHours,
  RoutedThroughCallRecording,
  RoutedThroughChatbot,
  RoutedThroughClose,
  RoutedThroughContactForm,
  RoutedThroughDialInput,
  RoutedThroughFilter,
  RoutedThroughForward,
  RoutedThroughIntegration,
  RoutedThroughIntentDetection,
  RoutedThroughIVRMenu,
  RoutedThroughLocation,
  RoutedThroughMessengerInput,
  RoutedThroughMessengerMenu,
  RoutedThroughMessengerMessage,
  RoutedThroughMessengerUrlRouting,
  RoutedThroughQueue,
  RoutedThroughTags,
  RoutedThroughVoicemail,
} from './routedThroughNodes';

type Props = {
  entry: RoutedThroughFlowEntry;
};

function getRoutedNode(node: ConversationFlowActivitiesNode): ReactNode {
  switch (node.node_type) {
    case 'externaldatarouting':
      return <RoutedThroughFilter node={node} />;
    case 'phonenumber':
      return <RoutedThroughLocation node={node} />;
    case 'ivrmenu':
      return <RoutedThroughIVRMenu node={node} />;
    case 'businesshours':
      return <RoutedThroughBusinessHours node={node} />;
    case 'messengermenu':
      return <RoutedThroughMessengerMenu node={node} />;
    case 'queue':
      return <RoutedThroughQueue node={node} />;
    case 'forward':
      return <RoutedThroughForward node={node} />;
    case 'voicemail':
      return <RoutedThroughVoicemail node={node} />;
    case 'fetchexternaldata':
      return <RoutedThroughIntegration node={node} />;
    case 'callrecording':
      return <RoutedThroughCallRecording node={node} />;
    case 'autoreply':
      return <RoutedThroughAutoReply node={node} />;
    case 'tags':
      return <RoutedThroughTags node={node} />;
    case 'intentdetection':
      return <RoutedThroughIntentDetection node={node} />;
    case 'agentavailability':
      return <RoutedThroughAgentAvailability node={node} />;
    case 'externaldataphoneinput':
      return <RoutedThroughDialInput node={node} />;
    case 'messengerinputfield':
      return <RoutedThroughMessengerInput node={node} />;
    case 'messengerurlrouting':
      return <RoutedThroughMessengerUrlRouting node={node} />;
    case 'messengermessage':
      return <RoutedThroughMessengerMessage node={node} />;
    case 'contactform':
    case 'messengerformascontactform':
      return <RoutedThroughContactForm node={node} />;
    case 'announcement':
      return <RoutedThroughAnnouncement node={node} />;
    case 'chatbotnode':
      return <RoutedThroughChatbot node={node} />;
    case 'close':
      return <RoutedThroughClose node={node} />;
    default:
      return 'Unrecognized action';
  }
}

function getRoutedGroupedNodes(
  nodes: ConversationFlowActivitiesNode[],
): ReactNode {
  if (nodes.length === 0) {
    return (
      <Text.Paragraph variant="2" subdued>
        Unrecognized action
      </Text.Paragraph>
    );
  }
  const lastNode = nodes[nodes.length - 1];
  switch (lastNode.node_type) {
    case 'chatbotnode':
      return (
        <Text.Paragraph variant="2" subdued>
          <RoutedThroughChatbot node={lastNode} />
        </Text.Paragraph>
      );
    default:
      return nodes.map((node, index) => (
        <Text.Paragraph key={index} variant="2" subdued>
          {getRoutedNode(node)}
        </Text.Paragraph>
      ));
  }
}

const channelPathMap: Record<string, string> = {
  phone: 'phone',
  email: 'email',
  messenger: 'dixa-messenger',
};

export const RoutedThroughFlow = ({ entry }: Props): JSX.Element => {
  const navigate = useAppNavigate();
  const { data } = conversationsApi.useReadConversationFlowActivitiesQuery({
    csid: entry.csid,
  });

  const hasFlowReadAccess = useHasAccess('settings.flow:r');

  const handleGoToFlow = () => {
    navigate({
      path: '/settings/:channel/flow/:flowId',
      params: {
        path: {
          channel: channelPathMap[entry.channel],
          flowId: entry.flow_id,
        },
      },
    });
  };

  /**
   * Returns a grouped array of flow activities. The grouping is only based on
   * consecutive node types. Non consecutive node types are not grouped
   * together.
   */
  const groupedActivities = useMemo(
    () =>
      data?.data.flow_activities.reduce(
        (acc: ConversationFlowActivitiesNode[][], node) => {
          const lastGroup: ConversationFlowActivitiesNode[] =
            acc[acc.length - 1];

          if (lastGroup && lastGroup[0].node_type === node.node_type) {
            lastGroup.push(node);
          } else {
            acc.push([node]);
          }

          return acc;
        },
        [],
      ),
    [data?.data.flow_activities],
  );

  return (
    <LogEntry
      entry={entry}
      icon="route"
      text={`Routed through ${entry.channel} flow ${entry.flow_name} ${
        ['phone', 'email'].includes(entry.channel)
          ? `for ${entry.location}`
          : ''
      }`}
      isExpandable
    >
      <FlexBox
        padding="0 0 0 300"
        margin="0 0 0 500"
        gap="200"
        flexDirection="column"
      >
        {groupedActivities?.map((group) => getRoutedGroupedNodes(group))}

        {hasFlowReadAccess && (
          <FlexBox margin="300 0">
            <Button variant="secondary" onClick={handleGoToFlow}>
              Go to flow
            </Button>
          </FlexBox>
        )}
      </FlexBox>
    </LogEntry>
  );
};
